/**
 * Mellow Cosmetics — Native Cart Drawer
 * Fully custom AJAX cart drawer with a built-in Gift-With-Purchase
 * reward tracker. No third-party cart or GWP app involved.
 *
 * GWP discounts themselves are applied by Shopify's own native
 * Buy X Get Y automatic discounts (server-side, always accurate).
 * This script's job is only to: render the cart, drive AJAX cart
 * calls, and show progress/messaging toward the next reward.
 */
(function () {
  "use strict";

  var configEl = document.getElementById("mellow-cart-config");
  if (!configEl) return;
  var CONFIG = JSON.parse(configEl.textContent);

  var drawer = document.getElementById("mellow-cart-drawer");
  var overlay = document.getElementById("mellow-cart-overlay");
  var closeBtn = document.getElementById("mellow-cart-close");

  // Force the header into a flex row directly via inline styles as a
  // bulletproof fallback — this can never be affected by a stale/
  // cached CSS file, since it's applied fresh by this script on
  // every load. Avoids relying on absolute positioning for the close
  // button entirely, which proved unreliable against other page CSS.
  var headerEl = document.querySelector(".mellow-cart__header");
  if (headerEl) {
    headerEl.style.display = "flex";
    headerEl.style.alignItems = "center";
    headerEl.style.justifyContent = "space-between";
    headerEl.style.boxSizing = "border-box";
  }
  if (closeBtn) {
    closeBtn.style.position = "static";
    closeBtn.style.flexShrink = "0";
    closeBtn.style.margin = "0";
    closeBtn.style.width = "32px";
    closeBtn.style.height = "32px";
  }

  var itemsEl = document.getElementById("mellow-cart-items");
  var bodyEl = document.querySelector(".mellow-cart__body");
  var emptyEl = document.getElementById("mellow-cart-empty");
  var countEl = document.getElementById("mellow-cart-count");
  var subtotalEl = document.getElementById("mellow-cart-subtotal");
  var totalEl = document.getElementById("mellow-cart-total");
  var discountRowEl = document.getElementById("mellow-cart-discount-row");
  var discountAmountEl = document.getElementById("mellow-cart-discount-amount");
  var checkoutTotalEl = document.getElementById("mellow-cart-checkout-total");
  var rewardsFillEl = document.getElementById("mellow-cart-rewards-fill");
  var rewardsMsgEl = document.getElementById("mellow-cart-rewards-message");
  var rewardsShippingMsgEl = document.getElementById("mellow-cart-rewards-shipping-message");
  var noteInputEl = document.getElementById("mellow-cart-note-input");
  var noteStatusEl = document.getElementById("mellow-cart-note-status");
  var recSection = document.getElementById("mellow-cart-recommendations");
  var recList = document.getElementById("mellow-cart-recommendations-list");

  var lastFocusedEl = null;
  var eligibleIds = (CONFIG.eligibleProductIds || []).map(function (id) {
    return String(id).replace(/\D/g, "");
  });

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function clamp01(n) {
    if (isNaN(n) || n < 0) return 0;
    if (n > 1) return 1;
    return n;
  }

  function formatMoney(cents) {
    // Lightweight formatter driven by the shop's money_format setting.
    var format = CONFIG.moneyFormat || "${{amount}}";
    var value = (cents / 100).toFixed(2);
    var withThousands = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return format
      .replace("{{amount}}", withThousands)
      .replace("{{amount_no_decimals}}", Math.round(cents / 100).toString());
  }

  /* ---------------------------------------------------------------
   * Drawer open / close, focus trap, ESC to close
   * ------------------------------------------------------------- */
  function openDrawer() {
    lastFocusedEl = document.activeElement;
    drawer.hidden = false;
    overlay.hidden = false;
    // Always start scrolled to the top — otherwise the drawer can
    // reopen wherever it was last scrolled to (e.g. down at the
    // upsells), hiding a product that was just added to the cart.
    if (bodyEl) bodyEl.scrollTop = 0;
    requestAnimationFrame(function () {
      drawer.classList.add("is-open");
      overlay.classList.add("is-visible");
    });
    document.body.style.overflow = "hidden";
    closeBtn.focus();
    document.addEventListener("keydown", handleKeydown);
  }

  function closeDrawer() {
    // If we're on the standalone /cart page (only reachable by landing
    // there directly, e.g. from checkout's cart icon), closing the
    // drawer would otherwise reveal the old, non-functional static
    // cart page underneath — send them somewhere useful instead.
    var cartPagePath = window.location.pathname.replace(/\/$/, "");
    if (cartPagePath === "/cart") {
      window.location.href = "/";
      return;
    }

    drawer.classList.remove("is-open");
    overlay.classList.remove("is-visible");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", handleKeydown);
    setTimeout(function () {
      drawer.hidden = true;
      overlay.hidden = true;
    }, CONFIG.animationSpeed || 300);
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      closeDrawer();
      return;
    }
    if (event.key === "Tab") {
      var focusable = drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  closeBtn.addEventListener("click", closeDrawer);
  overlay.addEventListener("click", closeDrawer);

  document.addEventListener(
    "click",
    function (event) {
      // [data-mellow-cart-open] is for any new markup we add ourselves.
      // [data-wau-slideout-target="ajax-cart"] is the theme's existing
      // header cart icon. .mobile-menu__cart-icon is the cart link
      // inside the mobile nav dropdown, which otherwise just navigates
      // to the theme's default (unstyled) /cart page.
      var trigger =
        event.target.closest("[data-mellow-cart-open]") ||
        event.target.closest('[data-wau-slideout-target="ajax-cart"]') ||
        event.target.closest(".mobile-menu__cart-icon");
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      // If the mobile nav menu is open, close it first via its own
      // existing close control so the handoff to the cart feels clean.
      var mobileNavClose = document.querySelector(
        "#slideout-mobile-navigation .js-slideout-close"
      );
      if (mobileNavClose) mobileNavClose.click();

      renderCart();
      openDrawer();
    },
    true
  );

  /* ---------------------------------------------------------------
   * Add to Cart — intercepted universally so every product form
   * (main product page, quick-view, quick-add) opens this drawer.
   * Requests are serialized (one at a time) rather than fired
   * concurrently — adding the same variant twice in quick succession
   * (e.g. rapid clicks) can otherwise race and cause Shopify to
   * create a second separate line instead of merging the quantity
   * into the existing one.
   * ------------------------------------------------------------- */
  var addToCartChain = Promise.resolve();

  // Some upsell/quick-add widgets elsewhere on the page (e.g. a
  // product page's "you may also like" section) add to cart via
  // their own JS calling fetch() directly, rather than submitting a
  // normal <form action="/cart/add">. Those wouldn't be caught by
  // the submit listener below at all, so the drawer would never
  // know a product was added. Wrapping fetch itself catches any
  // successful add-to-cart request site-wide, no matter what
  // triggered it, and opens the drawer to match.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var method = ((init && init.method) || (typeof input === "object" && input.method) || "GET").toUpperCase();
    var isAddToCart = method === "POST" && url.indexOf("/cart/add") !== -1;

    return nativeFetch.apply(this, arguments).then(function (response) {
      if (isAddToCart && response.ok) {
        response
          .clone()
          .json()
          .then(function () {
            renderCart();
            openDrawer();
          })
          .catch(function () {
            renderCart();
            openDrawer();
          });
      }
      return response;
    });
  };

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target.closest("form");
      if (!form) return;
      var action = form.getAttribute("action") || "";
      if (action.indexOf("/cart/add") === -1) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      var formData = new FormData(form);

      addToCartChain = addToCartChain
        .then(function () {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { Accept: "application/json" },
            body: formData,
          });
        })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        })
        .then(function () {
          renderCart();
          openDrawer();
        })
        .catch(function (err) {
          console.error("[mellow-cart] add to cart failed", err);
          var message =
            (err && err.description) || "That item couldn't be added to your bag.";
          alert(message);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    },
    true
  );

  /* ---------------------------------------------------------------
   * Quantity + / - / manual input / remove — delegated, key-based
   * ------------------------------------------------------------- */
  function changeQuantity(key, quantity) {
    return fetch(CONFIG.routes.cartChangeUrl || "/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (data) {
          throw data;
        });
      }
      return res.json();
    });
  }

  // Sends immediately (no artificial delay) but guarantees only one
  // request per item key is in flight at a time. If more clicks come
  // in while a request is running, only the LATEST desired quantity
  // is remembered and sent right after — this avoids both the
  // "feels slow" problem (waiting on a debounce timer) and the
  // out-of-order write problem (overlapping requests racing).
  //
  // If a request ever fails for any reason, we still re-render from
  // the real server state immediately, so the cart can never get
  // stuck showing something that isn't actually true anymore —
  // fixing the "have to refresh 3 times" issue.
  var keyState = {};

  function sendQuantity(key, quantity) {
    var state = keyState[key] || (keyState[key] = { busy: false, latestQty: null });
    state.latestQty = quantity;
    if (state.busy) return;
    processQueue(key);
  }

  function processQueue(key) {
    var state = keyState[key];
    if (!state || state.latestQty === null) return;
    var qty = state.latestQty;
    state.latestQty = null;
    state.busy = true;

    changeQuantity(key, qty)
      .then(function (cart) {
        state.busy = false;

        // Verify a removal actually took effect. The GWP tiers can
        // occasionally reallocate which line is discounted as the
        // cart changes, which shifts that line's key — if that
        // happens between the last render and this click, the
        // removal request targets a key that's already gone stale.
        // Retry once automatically so Remove never silently fails.
        if (qty === 0 && cart && cart.items) {
          var stillThere = cart.items.some(function (i) {
            return i.key === key;
          });
          if (stillThere && !state.retried) {
            state.retried = true;
            state.latestQty = 0;
            processQueue(key);
            return;
          }
        }

        renderCart();
        if (state.latestQty !== null) processQueue(key);
      })
      .catch(function (err) {
        console.error("[mellow-cart] quantity update failed", err);
        state.busy = false;
        renderCart(); // self-heal: show what's actually true, don't leave it stale
        if (state.latestQty !== null) processQueue(key);
      });
  }

  // Adds or removes quantity for a merged group. Adding always goes
  // through /cart/add.js (Shopify decides which underlying line it
  // belongs on); removing takes from the group's lines starting with
  // the last one, cascading to earlier lines if needed.
  function adjustGroupQuantity(variantId, keys, delta) {
    if (!delta) return;
    if (delta > 0) {
      addToCartChain = addToCartChain
        .then(function () {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: variantId, quantity: delta }),
          });
        })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        })
        .then(function () {
          renderCart();
        })
        .catch(function (err) {
          console.error("[mellow-cart] add failed", err);
          renderCart();
        });
      return;
    }

    if (!lastCart) return;
    var remaining = -delta;
    var lines = lastCart.items.filter(function (i) {
      return keys.indexOf(i.key) !== -1;
    });
    for (var i = lines.length - 1; i >= 0 && remaining > 0; i--) {
      var take = Math.min(remaining, lines[i].quantity);
      sendQuantity(lines[i].key, lines[i].quantity - take);
      remaining -= take;
    }
  }

  // Removes an exact quantity of a variant + price category, one
  // line at a time, re-checking the real cart state before each
  // step. Removing several lines independently (e.g. all keys
  // captured at render time) can fail partway through: removing one
  // line changes the GWP discount recalculation, which can reassign
  // or invalidate remaining target keys — or even convert other,
  // previously-free units of the same variant into this same
  // category as a side effect. Tracking an exact quantity (rather
  // than sweeping the category until it's empty) means we stop once
  // we've removed what was actually asked for, not anything that
  // happens to land in the same bucket afterward.
  function removeGroupSequential(variantId, category, remaining) {
    if (remaining <= 0) {
      renderCart();
      return;
    }
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var match = cart.items.find(function (i) {
          return String(i.variant_id) === String(variantId) && priceCategory(i) === category;
        });
        if (!match) {
          renderCart();
          return;
        }
        var take = Math.min(remaining, match.quantity);
        return changeQuantity(match.key, match.quantity - take).then(function () {
          return removeGroupSequential(variantId, category, remaining - take);
        });
      })
      .catch(function (err) {
        console.error("[mellow-cart] group removal failed", err);
        renderCart();
      });
  }

  itemsEl.addEventListener("click", function (event) {
    var incBtn = event.target.closest("[data-qty-increase]");
    var decBtn = event.target.closest("[data-qty-decrease]");
    var removeBtn = event.target.closest("[data-remove-item]");

    if (removeBtn) {
      var removeVariantId = removeBtn.getAttribute("data-variant-id");
      var removeCategory = removeBtn.getAttribute("data-category");
      var removeQty = parseInt(removeBtn.getAttribute("data-qty"), 10) || 0;
      // Instant feedback so it never feels unresponsive, even if the
      // network call takes a moment — the row visibly fades right
      // away, and the follow-up render always reflects the true
      // cart state (correcting itself if anything went wrong).
      var removeRow = removeBtn.closest("[data-item-row]");
      if (removeRow) {
        removeRow.style.opacity = "0.4";
        removeRow.style.pointerEvents = "none";
      }
      removeBtn.disabled = true;
      removeGroupSequential(removeVariantId, removeCategory, removeQty);
      return;
    }

    var btn = incBtn || decBtn;
    if (!btn) return;

    var variantId = btn.getAttribute("data-variant-id");
    var keys = (btn.getAttribute("data-keys") || "").split(",").filter(Boolean);
    var row = btn.closest("[data-item-row]");
    var input = row && row.querySelector("[data-qty-input]");
    if (!input) return;

    var qty = parseInt(input.value, 10) || 0;
    qty = incBtn ? qty + 1 : Math.max(0, qty - 1);
    input.value = qty; // optimistic UI, updates instantly on every click

    adjustGroupQuantity(variantId, keys, incBtn ? 1 : -1);
  });

  itemsEl.addEventListener(
    "change",
    function (event) {
      var input = event.target.closest("[data-qty-input]");
      if (!input) return;
      var variantId = input.getAttribute("data-variant-id");
      var keys = (input.getAttribute("data-keys") || "").split(",").filter(Boolean);
      var newQty = parseInt(input.value, 10);
      if (isNaN(newQty) || newQty < 0) newQty = 0;

      var oldQty = 0;
      if (lastCart) {
        lastCart.items.forEach(function (i) {
          if (keys.indexOf(i.key) !== -1) oldQty += i.quantity;
        });
      }

      adjustGroupQuantity(variantId, keys, newQty - oldQty);
    }
  );

  /* ---------------------------------------------------------------
   * Compare-at price lookup — cart.js doesn't include compare_at_price
   * by default, so we fetch it once per product (cached) via the
   * public product JSON endpoint and merge it into line item display.
   * ------------------------------------------------------------- */
  var productDataCache = {};
  var productDataResolved = {};

  function fetchProductData(handle) {
    if (productDataCache[handle]) return productDataCache[handle];
    productDataCache[handle] = fetch("/products/" + handle + ".js")
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        productDataResolved[handle] = data;
        return data;
      })
      .catch(function () {
        productDataResolved[handle] = null;
        return null;
      });
    return productDataCache[handle];
  }

  function getCompareAtCents(item) {
    var product = productDataResolved[item.handle];
    if (!product || !product.variants) return null;
    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === item.variant_id) {
        variant = product.variants[i];
        break;
      }
    }
    if (!variant || !variant.compare_at_price) return null;
    var cents = parseInt(variant.compare_at_price, 10);
    return isNaN(cents) ? null : cents;
  }

  // The cart's "was" subtotal: everything at its full original value
  // (compare-at sale price if one exists, otherwise regular price).
  // Using this consistently for Subtotal means Subtotal - Discount
  // always equals Total, rather than the two figures being computed
  // from different baselines.
  function computeWasSubtotal(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      var compareAtCents = getCompareAtCents(item);
      var wasPrice =
        compareAtCents && compareAtCents > item.original_price
          ? compareAtCents
          : item.original_price;
      total += wasPrice * item.quantity;
    });
    return total;
  }

  var lastCart = null;

  // Shopify's discount engine sometimes splits a variant across
  // multiple cart lines as it recalculates, including transient
  // rounding differences between lines that are really the same
  // price point. Group by variant + discount category (free /
  // discounted / full price) rather than an exact cent match, so
  // the customer sees one row per genuinely distinct price tier —
  // e.g. "4 paid" and "1 free" as two rows — regardless of minor
  // per-line rounding noise Shopify may produce along the way.
  function priceCategory(item) {
    if (item.final_price === 0 && item.original_price > 0) return "free";
    if (item.final_price < item.original_price) return "discounted";
    return "full";
  }

  function groupCartItems(cart) {
    var groups = {};
    var order = [];
    cart.items.forEach(function (item) {
      var mergeKey = item.variant_id + ":" + priceCategory(item);
      if (!groups[mergeKey]) {
        groups[mergeKey] = {
          mergeKey: mergeKey,
          variantId: item.variant_id,
          keys: [],
          quantity: 0,
          finalLineTotal: 0,
          representative: item,
        };
        order.push(mergeKey);
      }
      var g = groups[mergeKey];
      g.keys.push(item.key);
      g.quantity += item.quantity;
      g.finalLineTotal += item.final_line_price;
      // Keep the representative with the highest quantity so the
      // displayed unit price reflects the dominant line, not
      // whichever line happened to be created first.
      if (item.quantity > g.representative.quantity) g.representative = item;
    });
    return order.map(function (k) {
      return groups[k];
    });
  }

  /* ---------------------------------------------------------------
   * Cart rendering
   * ------------------------------------------------------------- */
  function itemTemplate(group) {
    var item = group.representative;
    var qty = group.quantity;
    var compareAtCents = getCompareAtCents(item);
    // "Was" price is whichever is higher: the product's own compare-at
    // sale price, or the pre-cart-discount price — so a strikethrough
    // always reflects the full original value, cascading correctly
    // when a further GWP tier discount also applies on top. Shown as
    // a total for the whole merged line (was-price × quantity), so a
    // line with 2 free units correctly shows $50 struck through, not
    // just a single unit's $25.
    var wasPricePerUnit =
      compareAtCents && compareAtCents > item.original_price
        ? compareAtCents
        : item.original_price;
    var wasTotal = wasPricePerUnit * qty;
    var compareAtTotal = compareAtCents ? compareAtCents * qty : null;
    var originalTotal = item.original_price * qty;

    var isFree = item.final_price === 0 && item.original_price > 0;
    // A cart-level GWP discount reduces price below the item's own
    // regular selling price (original_price already reflects any
    // normal compare-at sale) — that's distinct from just having a
    // compare-at sale with no further tier discount applied.
    var hasCartDiscount = item.final_price < item.original_price;
    // True when the product already had its own regular sale price
    // (compare-at) BEFORE any GWP tier discount was applied on top —
    // this is the case where we want to show both "was" prices
    // stacked, so the customer can see they stacked two discounts.
    var hasSaleBeforeCart = compareAtCents && compareAtCents > item.original_price;
    var hasAnyDiscount = isFree || wasPricePerUnit > item.final_price;

    var variantLine =
      item.variant_title && item.variant_title !== "Default Title"
        ? '<p class="mellow-cart__item-variant">' + item.variant_title + "</p>"
        : "";

    var priceParts = [];
    if (hasSaleBeforeCart && (hasCartDiscount || isFree)) {
      // Show both "was" prices stacked: the original compare-at
      // price, then the regular sale price it was already reduced
      // to — so a further GWP discount on top is clearly visible
      // as a second, additional saving rather than replacing it.
      priceParts.push(
        '<span class="mellow-cart__item-compare mellow-cart__item-compare--outer">' +
          formatMoney(compareAtTotal) +
          "</span>"
      );
      priceParts.push(
        '<span class="mellow-cart__item-compare">' + formatMoney(originalTotal) + "</span>"
      );
    } else if (hasCartDiscount || isFree) {
      priceParts.push(
        '<span class="mellow-cart__item-compare">' + formatMoney(wasTotal) + "</span>"
      );
    } else if (hasSaleBeforeCart) {
      priceParts.push(
        '<span class="mellow-cart__item-compare">' + formatMoney(compareAtTotal) + "</span>"
      );
    }

    if (isFree) {
      priceParts.push('<span class="mellow-cart__item-badge">FREE</span>');
    } else if (hasCartDiscount) {
      var percentLabel = (CONFIG.tier0EffectPercent || 50) + "% OFF";
      priceParts.push(
        '<span class="mellow-cart__item-badge mellow-cart__item-badge--percent">' + percentLabel + "</span>"
      );
      priceParts.push('<span class="mellow-cart__item-final">' + formatMoney(group.finalLineTotal) + "</span>");
    } else if (hasAnyDiscount) {
      priceParts.push('<span class="mellow-cart__item-final">' + formatMoney(group.finalLineTotal) + "</span>");
    } else {
      priceParts.push("<span>" + formatMoney(group.finalLineTotal) + "</span>");
    }

    var priceRow = '<div class="mellow-cart__item-price-row">' + priceParts.join("") + "</div>";

    var image = item.image
      ? '<img src="' + item.image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, "_200x$1$2") + '" alt="' + (item.product_title || "").replace(/"/g, "&quot;") + '" loading="lazy">'
      : "";

    var keysAttr = group.keys.join(",");

    return (
      '<div class="mellow-cart__item" data-item-row data-merge-key="' + group.mergeKey + '">' +
      '<div class="mellow-cart__item-image"><a href="' + item.url + '">' + image + "</a></div>" +
      '<div class="mellow-cart__item-details">' +
      '<p class="mellow-cart__item-title"><a href="' + item.url + '">' + item.product_title + "</a></p>" +
      variantLine +
      priceRow +
      '<div class="mellow-cart__item-controls">' +
      '<div class="mellow-cart__qty-group">' +
      '<div class="mellow-cart__qty">' +
      '<button type="button" data-qty-decrease data-variant-id="' + group.variantId + '" data-keys="' + keysAttr + '" aria-label="Decrease quantity">\u2212</button>' +
      '<input type="number" min="0" value="' + group.quantity + '" data-qty-input data-variant-id="' + group.variantId + '" data-keys="' + keysAttr + '" aria-label="Quantity">' +
      '<button type="button" data-qty-increase data-variant-id="' + group.variantId + '" data-keys="' + keysAttr + '" aria-label="Increase quantity">+</button>' +
      "</div>" +
      '<button type="button" class="mellow-cart__item-remove" data-remove-item data-variant-id="' + group.variantId + '" data-category="' + priceCategory(item) + '" data-qty="' + group.quantity + '" aria-label="Remove item">' +
      '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M2.5 4.5H13.5M6 4.5V2.75C6 2.34 6.34 2 6.75 2H9.25C9.66 2 10 2.34 10 2.75V4.5M6.5 7.5V11.5M9.5 7.5V11.5M3.5 4.5L4 12.75C4.03 13.44 4.6 14 5.3 14H10.7C11.4 14 11.97 13.44 12 12.75L12.5 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>" +
      "</button>" +
      "</div>" +
      "</div>" +
      "</div>" +
      "</div>"
    );
  }

  function renderItems(cart) {
    if (!cart.items.length) {
      itemsEl.innerHTML = "";
      emptyEl.hidden = false;
      return;
    }
    emptyEl.hidden = true;
    itemsEl.innerHTML = groupCartItems(cart).map(itemTemplate).join("");
  }

  function updateDiscountRow(cart, wasSubtotal) {
    var totalSavings = wasSubtotal - cart.total_price;
    if (totalSavings > 0) {
      discountRowEl.hidden = false;
      discountAmountEl.innerHTML = "-" + formatMoney(totalSavings);
    } else {
      discountRowEl.hidden = true;
    }
  }

  function renderTotals(cart) {
    countEl.textContent = cart.item_count;

    var wasSubtotal = computeWasSubtotal(cart);

    // IMPORTANT: shop.money_format can itself contain HTML (e.g.
    // <span class="money">{{amount}}</span>), so this must be
    // injected as HTML, not as plain text, or the tags show up
    // literally on the page instead of being rendered.
    subtotalEl.innerHTML = formatMoney(wasSubtotal);
    totalEl.innerHTML = formatMoney(cart.total_price);
    checkoutTotalEl.innerHTML = formatMoney(cart.total_price);

    updateDiscountRow(cart, wasSubtotal);

    // Keep the theme's existing header cart-count badge(s) in sync too.
    document.querySelectorAll(".js-cart-count").forEach(function (el) {
      el.textContent = cart.item_count;
    });
  }

  /* ---------------------------------------------------------------
   * Order note — debounced auto-save, doesn't stomp on active typing
   * ------------------------------------------------------------- */
  function renderNote(cart) {
    if (!noteInputEl) return;
    // Don't overwrite while the customer is actively typing/focused
    // in the field, or a re-render (e.g. from a quantity change
    // elsewhere) could wipe out what they're mid-way through writing.
    if (document.activeElement === noteInputEl) return;
    noteInputEl.value = cart.note || "";
  }

  var noteSaveTimer = null;

  function saveNote() {
    if (!noteInputEl) return;
    if (noteSaveTimer) clearTimeout(noteSaveTimer);
    noteSaveTimer = setTimeout(function () {
      if (noteStatusEl) noteStatusEl.textContent = "Saving...";
      fetch(CONFIG.routes.cartUpdateUrl || "/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteInputEl.value }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          if (noteStatusEl) {
            noteStatusEl.textContent = "Saved";
            setTimeout(function () {
              if (noteStatusEl.textContent === "Saved") noteStatusEl.textContent = "";
            }, 1500);
          }
        })
        .catch(function (err) {
          console.error("[mellow-cart] note save failed", err);
          if (noteStatusEl) noteStatusEl.textContent = "Couldn't save — try again";
        });
    }, 600);
  }

  if (noteInputEl) {
    noteInputEl.addEventListener("input", saveNote);
    noteInputEl.addEventListener("blur", function () {
      if (noteSaveTimer) clearTimeout(noteSaveTimer);
      saveNote();
    });
  }

  /* ---------------------------------------------------------------
   * Reward tracker — Tier 1 / Tier 2 / Free shipping
   * ------------------------------------------------------------- */
  function eligibleQuantity(cart) {
    var qty = 0;
    cart.items.forEach(function (item) {
      var pid = String(item.product_id);
      if (eligibleIds.indexOf(pid) !== -1) {
        qty += item.quantity;
      }
    });
    return qty;
  }

  function hexToRgb(hex) {
    var clean = hex.replace("#", "");
    if (clean.length === 3) {
      clean = clean
        .split("")
        .map(function (c) {
          return c + c;
        })
        .join("");
    }
    var num = parseInt(clean, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }

  function interpolateColor(hex1, hex2, t) {
    var c1 = hexToRgb(hex1);
    var c2 = hexToRgb(hex2);
    var r = Math.round(c1.r + (c2.r - c1.r) * t);
    var g = Math.round(c1.g + (c2.g - c1.g) * t);
    var b = Math.round(c1.b + (c2.b - c1.b) * t);
    return "rgb(" + r + "," + g + "," + b + ")";
  }

  // This store's "International" market presents shoppers with their
  // own local currency automatically (dozens of possible currencies),
  // but only 6 have a merchant-configured exact threshold matching
  // real Shopify shipping rates. For anything else, approximate a
  // sensible equivalent from the AUD threshold rather than showing
  // no shipping message at all. These are rough, static conversion
  // ratios (units of that currency per 1 AUD) — accurate enough to
  // give every customer a meaningful reference point, though they'll
  // drift from live exchange rates over time.
  var APPROX_UNITS_PER_AUD = {
    JPY: 97, SGD: 0.87, HKD: 5.1, CHF: 0.58, CNY: 4.7, INR: 55,
    MXN: 12.5, BRL: 3.7, ZAR: 12.2, SEK: 6.9, NOK: 7.0, DKK: 4.55,
    PLN: 2.6, CZK: 15.5, HUF: 240, ILS: 2.4, AED: 2.4, SAR: 2.5,
    THB: 23, MYR: 2.9, IDR: 10400, PHP: 37, VND: 17000, KRW: 900,
    TWD: 21, TRY: 21.5, CLP: 630, COP: 2650, ARS: 620, RON: 3.05,
    BGN: 1.19, ISK: 92, HRK: 4.6, RSD: 71.5, UAH: 27.5, EGP: 32.5,
    KWD: 0.2, QAR: 2.4, BHD: 0.25, OMR: 0.25, PKR: 183, LKR: 195,
    BDT: 78, NGN: 1020, KES: 85, GHS: 9.7, VES: 24, PEN: 2.45,
    UYU: 27, CRC: 340, DOP: 39, GTQ: 5.1, TND: 2.05,
  };

  // Live, daily-updated exchange rates for any currency outside the
  // 6 explicitly configured shipping zones — those 6 always use the
  // merchant's exact configured Shopify rate and never touch this.
  // A static approximation table drifts from real rates over time
  // and can genuinely mislead a customer (e.g. showing "free
  // shipping unlocked" in their local currency when the real
  // Shopify-side USD Rest-of-World rate hasn't actually been met
  // yet). Fetched once per page load and cached; falls back to the
  // static table below only if this hasn't resolved yet or fails.
  var liveExchangeRates = null;
  fetch("https://open.er-api.com/v6/latest/USD")
    .then(function (res) {
      return res.json();
    })
    .then(function (data) {
      if (data && data.result === "success" && data.rates) {
        liveExchangeRates = data.rates;
        // Re-render if the drawer is already open with a cart loaded,
        // so an accurate rate applies immediately rather than only
        // on the next add/remove.
        if (lastCart) renderRewards(lastCart);
      }
    })
    .catch(function (err) {
      console.error("[mellow-cart] live exchange rate fetch failed", err);
    });

  function approximateThreshold(currency) {
    // Rest-of-World shipping is configured in Shopify as a USD
    // amount — converting that exact USD figure using a live rate
    // is far more accurate than approximating from the AUD setting.
    var usdBase = (CONFIG.freeShippingThresholds || {}).USD;
    if (liveExchangeRates && usdBase && liveExchangeRates[currency]) {
      return Math.round(usdBase * liveExchangeRates[currency]);
    }
    var audBase = (CONFIG.freeShippingThresholds || {}).AUD;
    var rate = APPROX_UNITS_PER_AUD[currency];
    if (!audBase || !rate) return null;
    return Math.round(audBase * rate);
  }

  function renderRewards(cart) {
    var tier0Buy = CONFIG.tier0Quantity || 2;
    var tier0Free = CONFIG.tier0FreeQuantity || 1;
    var tier0Effect = CONFIG.tier0EffectPercent || 50;
    var tier1Buy = CONFIG.tier1Quantity || 3;
    var tier1Free = CONFIG.tier1FreeQuantity || 1;
    var tier2Buy = CONFIG.tier2Quantity || 4;
    var tier2Free = CONFIG.tier2FreeQuantity || 2;

    // These discounts don't stack with each other (combinesWith is
    // false on all three in Shopify) — only the highest tier a
    // customer qualifies for actually applies. Thresholds are
    // buy + get totals, matching Shopify's own BXGY math.
    var tier0Total = tier0Buy + tier0Free; // 50% OFF: needs 3
    var tier1Total = tier1Buy + tier1Free; // FREE: needs 4
    var tier2Total = tier2Buy + tier2Free; // FREE!: needs 6

    var currency = cart.currency || CONFIG.currency;
    var threshold = (CONFIG.freeShippingThresholds || {})[currency];
    if (!threshold) threshold = approximateThreshold(currency);
    var qty = eligibleQuantity(cart);
    var subtotal = cart.total_price / 100;

    var milestone1 = document.querySelector('[data-milestone="1"]');
    var milestone2 = document.querySelector('[data-milestone="2"]');
    var milestone3 = document.querySelector('[data-milestone="3"]');

    // Real-data-driven unlock flags: does the actual Shopify cart
    // data show this tier genuinely applied, rather than inferring
    // it purely from eligible item COUNT? Counting alone is
    // misleading for bundles — they count toward the buy threshold
    // (so qty can reach 3, 4, or 6) but can never themselves become
    // the discounted/free item, so an all-bundle cart could hit the
    // count without any real discount ever applying.
    var discountedUnitsInCart = 0;
    var freeUnitsInCart = 0;
    cart.items.forEach(function (item) {
      var cat = priceCategory(item);
      if (cat === "discounted") discountedUnitsInCart += item.quantity;
      if (cat === "free") freeUnitsInCart += item.quantity;
    });

    var tier0Unlocked = discountedUnitsInCart >= tier0Free;
    var tier1Unlocked = freeUnitsInCart >= tier1Free;
    var tier2Unlocked = freeUnitsInCart >= tier2Free;
    var shippingUnlocked = threshold ? subtotal >= threshold : false;

    milestone1.classList.toggle("is-unlocked", tier0Unlocked);
    milestone2.classList.toggle("is-unlocked", tier1Unlocked);
    milestone3.classList.toggle("is-unlocked", tier2Unlocked);

    // The bar only jumps to a milestone once that tier is actually
    // unlocked — no gradual/partial fill while items are added
    // toward it, so it stays put until the discount genuinely kicks
    // in, then moves directly to that stage.
    var overall;
    if (tier2Unlocked) overall = 1;
    else if (tier1Unlocked) overall = 2 / 3;
    else if (tier0Unlocked) overall = 1 / 3;
    else overall = 0;

    rewardsFillEl.style.width = overall * 100 + "%";
    rewardsFillEl.style.background = interpolateColor(
      CONFIG.progressStartColor || "#f8dcd6",
      CONFIG.progressEndColor || "#ff3d78",
      overall
    );

    // Determine what would actually happen if one more eligible
    // (non-bundle) item were added right now. Because discounts
    // don't stack, bundles already sitting in the cart can push the
    // buy-count past more than one threshold simultaneously — so
    // simply assuming the next reward is the very next tier (e.g.
    // 50% off) isn't always correct; it might jump straight to FREE.
    function predictedRewardLabel(nextQty) {
      if (nextQty >= tier2Total) return tier2Free + " FREE items";
      if (nextQty >= tier1Total) return "it FREE";
      if (nextQty >= tier0Total) return tier0Effect + "% off";
      return tier0Effect + "% off";
    }

    // Which tier is genuinely active right now, based on real data —
    // not assumed to progress in strict order. Bundles can push the
    // buy-count straight to a higher tier's threshold without ever
    // triggering a lower one (e.g. straight to FREE without 50%
    // off ever applying), so checking "is tier0 unlocked?" first
    // would show the wrong message — including a negative "add X
    // more" count — once that's happened.
    var currentLevel = 0;
    if (tier2Unlocked) currentLevel = 3;
    else if (tier1Unlocked) currentLevel = 2;
    else if (tier0Unlocked) currentLevel = 1;

    var tierPart = "";
    if (currentLevel === 0) {
      if (qty >= tier0Total) {
        tierPart =
          "Bundles are excluded from this discount — add a non-bundle product to unlock " +
          predictedRewardLabel(qty + 1) + ".";
      } else {
        var remaining0 = tier0Total - qty;
        tierPart =
          "Add " + remaining0 + " more product" + (remaining0 === 1 ? "" : "s") +
          " to get " + tier0Effect + "% off your " + ordinal(tier0Total) + " item.";
      }
    } else if (currentLevel === 1) {
      if (qty >= tier1Total) {
        tierPart =
          "🎉 " + tier0Effect + "% off your " + ordinal(tier0Total) +
          " item unlocked! Bundles are excluded from your FREE item — add a non-bundle product to unlock " +
          predictedRewardLabel(qty + 1) + ".";
      } else {
        var remaining1 = tier1Total - qty;
        tierPart =
          "🎉 " + tier0Effect + "% off your " + ordinal(tier0Total) +
          " item unlocked! Add " + remaining1 +
          " more to get your " + ordinal(tier1Total) + " item FREE.";
      }
    } else if (currentLevel === 2) {
      if (qty >= tier2Total) {
        tierPart =
          "🎉 1 FREE item unlocked! Bundles are excluded — add a non-bundle product to unlock " +
          tier2Free + " FREE items.";
      } else {
        var remaining2 = tier2Total - qty;
        tierPart =
          "🎉 1 FREE item unlocked! Add " + remaining2 +
          " more to unlock " + tier2Free + " FREE items.";
      }
    } else {
      tierPart = "🎉 " + tier2Free + " FREE items unlocked — best deal reached!";
    }

    var shippingPart = "";
    if (threshold) {
      if (shippingUnlocked) {
        shippingPart = "🚚 FREE shipping unlocked!";
      } else {
        var remainingShip = (threshold - subtotal).toFixed(2);
        shippingPart = "You are " + remainingShip + " " + currency + " away from unlocking FREE shipping.";
      }
    }

    rewardsMsgEl.textContent = tierPart;
    rewardsShippingMsgEl.textContent = shippingPart;
  }

  function ordinal(n) {
    var s = ["th", "st", "nd", "rd"];
    var v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  /* ---------------------------------------------------------------
   * Recommendations
   * ------------------------------------------------------------- */
  var featuredProductsPromise = null;

  function fetchFeaturedProducts() {
    if (featuredProductsPromise) return featuredProductsPromise;
    var handles = (CONFIG.featuredHandles || []).filter(Boolean);
    if (!handles.length) {
      featuredProductsPromise = Promise.resolve([]);
      return featuredProductsPromise;
    }
    featuredProductsPromise = Promise.all(
      handles.map(function (handle) {
        return fetch("/products/" + handle + ".js")
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (results) {
      return results.filter(Boolean);
    });
    return featuredProductsPromise;
  }

  // Pre-warm the pinned-products cache immediately on page load, in
  // the background, so it's already sitting ready by the time
  // someone actually opens the cart — instead of only starting that
  // fetch at click time and making them wait for it.
  fetchFeaturedProducts();

  function renderProductCards(products) {
    return products
      .map(function (product) {
        var img = product.featured_image
          ? product.featured_image
          : (product.images && product.images[0]) || "";
        var variant = product.variants && product.variants[0];
        var compareAt = variant && variant.compare_at_price
          ? parseInt(variant.compare_at_price, 10)
          : null;
        var priceHtml =
          compareAt && compareAt > product.price
            ? '<span class="mellow-cart__rec-compare">' + formatMoney(compareAt) + "</span> " +
              '<span class="mellow-cart__rec-final">' + formatMoney(product.price) + "</span>"
            : formatMoney(product.price);
        return (
          '<div class="mellow-cart__rec-card">' +
          '<a href="' + product.url + '"><img src="' + img + '" alt="' + product.title.replace(/"/g, "&quot;") + '" loading="lazy"></a>' +
          '<p class="mellow-cart__rec-title">' + product.title + "</p>" +
          '<p class="mellow-cart__rec-price">' + priceHtml + "</p>" +
          '<button type="button" class="mellow-cart__rec-add" data-rec-add="' + variant.id + '">Add To Cart</button>' +
          "</div>"
        );
      })
      .join("");
  }

  function renderRecommendations(cart) {
    if (!cart.items.length) {
      recSection.hidden = true;
      return;
    }

    var limit = CONFIG.recommendationsLimit || 4;
    var cartProductIds = cart.items.map(function (item) {
      return String(item.product_id);
    });

    fetchFeaturedProducts().then(function (rawFeatured) {
      // Don't show something as an upsell if it's already in the cart.
      var featured = rawFeatured
        .filter(function (p) {
          return cartProductIds.indexOf(String(p.id)) === -1;
        })
        .slice(0, limit);

      var remaining = Math.max(limit - featured.length, 0);
      if (remaining === 0) {
        recSection.hidden = false;
        recList.innerHTML = renderProductCards(featured);
        return;
      }

      var lastItem = cart.items[cart.items.length - 1];
      var fetchLimit = remaining + featured.length; // extra headroom for de-dupe

      var showCombined = function (algorithmic) {
        var featuredHandles = featured.map(function (p) {
          return p.handle;
        });
        var deduped = algorithmic.filter(function (p) {
          return (
            featuredHandles.indexOf(p.handle) === -1 &&
            cartProductIds.indexOf(String(p.id)) === -1
          );
        });
        var combined = featured.concat(deduped.slice(0, remaining));
        if (!combined.length) {
          recSection.hidden = true;
          return;
        }
        recSection.hidden = false;
        recList.innerHTML = renderProductCards(combined);
      };

      fetch(
        "/recommendations/products.json?product_id=" +
          lastItem.product_id +
          "&limit=" +
          fetchLimit +
          "&intent=complementary"
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          var products = data.products || [];
          if (!products.length) {
            return fetch(
              "/recommendations/products.json?product_id=" +
                lastItem.product_id +
                "&limit=" +
                fetchLimit +
                "&intent=related"
            )
              .then(function (res) {
                return res.json();
              })
              .then(function (fallbackData) {
                return fallbackData.products || [];
              });
          }
          return products;
        })
        .then(showCombined)
        .catch(function (err) {
          console.error("[mellow-cart] recommendations failed", err);
          // Still show the pinned/featured products even if the
          // algorithmic fetch fails entirely.
          if (featured.length) {
            recSection.hidden = false;
            recList.innerHTML = renderProductCards(featured);
          } else {
            recSection.hidden = true;
          }
        });
    });
  }

  recList &&
    recList.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-rec-add]");
      if (!btn) return;
      var variantId = btn.getAttribute("data-rec-add");
      btn.disabled = true;
      addToCartChain = addToCartChain
        .then(function () {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
        })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          renderCart();
          // Scroll back to the top so the newly added item is
          // immediately visible — otherwise the drawer stays
          // scrolled down at the upsells with no visible sign
          // anything was added.
          if (bodyEl) bodyEl.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch(function (err) {
          console.error("[mellow-cart] recommendation add failed", err);
        })
        .finally(function () {
          btn.disabled = false;
        });
    });

  /* ---------------------------------------------------------------
   * Master render
   * ------------------------------------------------------------- */
  var renderToken = 0;

  function renderCart() {
    var thisToken = ++renderToken;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        // If a newer render has started since this one began, this
        // response is stale (e.g. from rapid +/- clicks resolving
        // out of order) — discard it so the header count, item list,
        // and totals never get out of sync with each other.
        if (thisToken !== renderToken) return cart;
        lastCart = cart;
        renderItems(cart);
        renderTotals(cart);
        renderRewards(cart);
        renderRecommendations(cart);
        renderNote(cart);

        // Compare-at prices aren't in /cart.js, so the first paint
        // above uses whatever's already cached (instant). Any new
        // product handles get fetched now, then items re-render once
        // that data lands — keeps the cart feeling fast while still
        // showing accurate strikethrough pricing shortly after.
        var handles = [];
        cart.items.forEach(function (item) {
          if (!productDataCache[item.handle] && handles.indexOf(item.handle) === -1) {
            handles.push(item.handle);
          }
        });
        if (handles.length) {
          Promise.all(handles.map(fetchProductData)).then(function () {
            if (thisToken !== renderToken) return;
            renderItems(cart);
            renderTotals(cart);
          });
        }

        return cart;
      })
      .catch(function (err) {
        console.error("[mellow-cart] render failed", err);
      });
  }

  var debouncedRender = debounce(renderCart, 50);

  // Initial paint on page load so the drawer is accurate even if
  // opened via the header's cart icon without an add-to-cart first.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", debouncedRender);
  } else {
    debouncedRender();
  }

  // If someone lands directly on the theme's own /cart page (this is
  // the only page Shopify's hosted checkout can link back to — it
  // can't open our in-page drawer directly), automatically open the
  // drawer instead, so there's only ever one real cart experience
  // anywhere on the site, rather than falling back to the old,
  // now-unused static cart page underneath.
  var cartPagePath = window.location.pathname.replace(/\/$/, "");
  if (cartPagePath === "/cart") {
    // Hide the rest of the page entirely (header, footer, recently
    // viewed, the old broken cart section, everything) rather than
    // just the cart section — leaves a clean, plain backdrop behind
    // the drawer instead of mismatched storefront content. Note:
    // Shopify's hosted checkout is a separate, locked-down system
    // that can't be embedded here, so this is the closest equivalent.
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el.contains(drawer) || el.contains(overlay)) return;
      if (el.tagName === "SCRIPT" || el.tagName === "LINK" || el.tagName === "STYLE") return;
      el.style.display = "none";
    });
    document.body.style.background = "#fff";

    renderCart();
    openDrawer();
  }
})();
